<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'

interface Props {
  text?: string
  position?: 'top' | 'bottom' | 'left' | 'right'
  width?: string | number
  trigger?: 'hover' | 'click' | 'both'
}

const props = withDefaults(defineProps<Props>(), {
  position: 'top',
  trigger: 'hover',
})

const isVisible = ref(false)

const handleClickOutside = (e: MouseEvent) => {
  const target = e.target as HTMLElement
  if (!target.closest('.tooltip-container')) {
    isVisible.value = false
  }
}

onMounted(() => {
  if (props.trigger === 'click' || props.trigger === 'both') {
    document.addEventListener('click', handleClickOutside)
  }
})

onBeforeUnmount(() => {
  if (props.trigger === 'click' || props.trigger === 'both') {
    document.removeEventListener('click', handleClickOutside)
  }
})

const toggle = () => {
  if (props.trigger === 'click' || props.trigger === 'both') {
    isVisible.value = !isVisible.value
  }
}
</script>

<template>
    <div class="relative flex items-center tooltip-container"
        @mouseenter="(props.trigger === 'hover' || props.trigger === 'both') && (isVisible = true)"
        @mouseleave="(props.trigger === 'hover' || props.trigger === 'both') && (isVisible = false)"
        @click.stop="toggle">
        <slot />

        <transition name="fade">
            <div v-if="isVisible" class="absolute z-[999] pt-2 cursor-pointer" :class="[
                {
                    'bottom-full pb-2 left-1/2 -translate-x-1/2': props.position === 'top',
                    'top-full pt-2 left-1/2 -translate-x-1/2': props.position === 'bottom',
                    'right-full pr-2 top-1/2 -translate-y-1/2': props.position === 'left',
                    'left-full pl-2 top-1/2 -translate-x-1/2': props.position === 'right',
                }
            ]" :style="{ width: props.width ? props.width + 'px' : 'auto' }">
                <slot name="content">
                    <div
                        class="w-full p-[15px] italic text-start text-[#1b325a] bg-[#f5f7fa] rounded-lg whitespace-normal break-words">
                        {{ props.text }}
                    </div>
                </slot>
            </div>

        </transition>
    </div>
</template>

<style>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
